import { isRetryableNetworkError } from "@/lib/network-error";

export type MapboxMapErrorPresentation = {
  kind: "offline" | "auth" | "load";
  title: string;
  description: string;
};

type MapboxLikeError = {
  status?: number;
  message?: string;
};

const TILE_ERROR_STATUS = 404;
const AUTH_STATUSES = new Set([401, 403]);

function toMapboxLikeError(error: unknown): MapboxLikeError {
  if (error instanceof Error) {
    const status =
      "status" in error &&
      typeof (error as { status: unknown }).status === "number"
        ? (error as { status: number }).status
        : undefined;

    return { status, message: error.message };
  }

  if (typeof error === "object" && error !== null) {
    const record = error as MapboxLikeError;
    return {
      status: typeof record.status === "number" ? record.status : undefined,
      message:
        typeof record.message === "string" ? record.message : String(error),
    };
  }

  return { message: String(error) };
}

function isTileLevelError(error: MapboxLikeError): boolean {
  if (error.status === TILE_ERROR_STATUS) {
    return true;
  }

  const message = error.message?.toLowerCase() ?? "";
  return (
    message.includes("tile") ||
    message.includes("failed to fetch") ||
    message.includes("networkerror") ||
    message.includes("load failed")
  );
}

function isAuthError(error: MapboxLikeError): boolean {
  if (error.status !== undefined && AUTH_STATUSES.has(error.status)) {
    return true;
  }

  const message = error.message?.toLowerCase() ?? "";
  return message.includes("unauthorized") || message.includes("token");
}

export function resolveMapboxMapError(
  error: unknown,
  offline: boolean
): MapboxMapErrorPresentation | null {
  const mapboxError = toMapboxLikeError(error);

  if (offline || isRetryableNetworkError(error)) {
    return {
      kind: "offline",
      title: "Map unavailable offline",
      description: "Check your connection to load map tiles.",
    };
  }

  if (isAuthError(mapboxError)) {
    return {
      kind: "auth",
      title: "Map configuration error",
      description: "The map could not authenticate. Check the Mapbox token.",
    };
  }

  if (isTileLevelError(mapboxError)) {
    return null;
  }

  return {
    kind: "load",
    title: "Map failed to load",
    description: mapboxError.message ?? "Try refreshing the page.",
  };
}
